/**
 * StorageSection — onglet "Stockage" (Paramètres > Stockage).
 *
 * Spec 2026-07-18 (rev iter94, Feb 2026) — visible en lecture pour tout
 * admin. Écritures réservées au Premier Admin. Affiche :
 *   - Volumétrie totale du bucket Backblaze B2 + estimation coût mensuel
 *   - Nombre d'objets stockés
 *   - Top 10 des fichiers les plus lourds (lien vers tâche/document)
 *   - Historique mensuel (jusqu'à 12 mois) — apparaît dès que des
 *     snapshots quotidiens ont été capturés.
 *
 * Backend : GET /api/settings/storage/overview
 */
import { hasRight } from "@/components/crm/crmShared";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "@/lib/toast";
import { RefreshCw, HardDrive, DollarSign, FileText, Loader2, ExternalLink, TrendingUp, ArrowRightLeft, Eye } from "lucide-react";
import DeskConfirm from "@/components/crm/DeskConfirm";
import { Card } from "@/components/ui/card";
import EgressTopSources from "./EgressTopSources";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fmtBytes = (n) => {
  if (!n && n !== 0) return "—";
  const u = ["o", "Ko", "Mo", "Go", "To"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 2 : v < 100 && i > 0 ? 1 : 0)} ${u[i]}`;
};

const fmtMoney = (n, cur = "USD") => {
  if (n === null || n === undefined) return "—";
  const s = new Intl.NumberFormat("fr-FR", { style: "currency", currency: cur, minimumFractionDigits: 4 }).format(n);
  return s;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso; }
};

export default function StorageSection() {
  const { user } = useAuth();
  const canWrite = hasRight(user, "settings.storage"); // iter203 — droit de la matrice (plus de premier-admin en dur)
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [migrateOpen, setMigrateOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/settings/storage/overview");
      setData(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Impossible de récupérer les données de stockage.");
    } finally {
      setLoading(false);
    }
  };

  const migrate = async () => {
    setMigrating(true);
    try {
      const { data: result } = await api.post("/settings/storage/migrate-legacy");
      const total = (result.task_attachments?.migrated || 0) + (result.documents_field_files?.migrated || 0);
      const failed = (result.task_attachments?.failed || 0) + (result.documents_field_files?.failed || 0);
      if (failed > 0) {
        toast.warning("Migration partielle", {
          description: `${total} fichier(s) migré(s), ${failed} en échec. Consultez les logs backend`,
        });
      } else {
        toast.success("Migration terminée", {
          description: `${total} fichier(s) migré(s) vers Backblaze B2`,
        });
      }
      await load();
    } catch (err) {
      toast.error("Migration impossible", { description: err.response?.data?.detail || "" });
    } finally {
      setMigrating(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return (
      <Card className="p-10 bg-card border-vertec-borderDark flex flex-col items-center gap-3">
        <Loader2 size={28} className="animate-spin text-vertec-blue" />
        <div className="text-sm text-muted-foreground">Chargement des données Backblaze B2…</div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-8 bg-card border-status-danger/40" data-testid="storage-error">
        <div className="flex items-start gap-3">
          <HardDrive size={22} className="text-status-danger mt-1" />
          <div>
            <h3 className="text-lg font-semibold text-status-danger mb-1">Stockage indisponible</h3>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <Button variant="outline" size="sm" onClick={load} data-testid="storage-retry">
              <RefreshCw size={14} className="mr-1.5" /> Réessayer
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  const gb = (data.total_bytes || 0) / (1024 ** 3);
  const monthlyUsd = data.monthly_cost_usd || 0;
  const monthlyEur = data.monthly_cost_eur || 0;

  return (
    <div className="space-y-6" data-testid="storage-section">
      {!canWrite && (
        <div
          className="flex items-center gap-2 rounded-lg px-4 py-2.5 text-[13px]"
          style={{
            background: "rgba(59,130,246,0.08)",
            border: "1px solid rgba(59,130,246,0.25)",
            color: "#93C5FD",
          }}
          data-testid="storage-readonly-banner"
        >
          <Eye size={14} />
          <span>
            Mode <strong>lecture seule</strong> — le Premier Admin seul peut lancer une
            migration ou toute action destructive sur le stockage.
          </span>
        </div>
      )}
      {/* --- Header --- */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold mb-1">Stockage applicatif — Fichiers utilisateurs</h2>
          <p className="text-sm text-muted-foreground">
            Fichiers uploadés depuis l'app (documents, pièces jointes, AVI, avatars) — préfixe <code className="text-vertec-blue">le-desk/</code> du bucket <code className="text-vertec-blue">{data.bucket}</code>.
            <span className="block text-[11px] mt-0.5 opacity-80">
              Les sauvegardes Mongo (préfixe <code>_backups/</code>) sont comptabilisées séparément dans <em>Backblaze B2 (sauvegardes)</em>.
            </span>
          </p>
          <p className="text-[11px] text-muted-foreground mt-1">
            Actualisé le {new Date(data.captured_at).toLocaleString("fr-FR")} · Coût = {data.pricing.storage_usd_per_gb_month} $/Go/mois · Egress = {data.pricing.egress_usd_per_gb} $/Go
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data?.legacy_pending > 0 && (
            <>
              <Button
                variant="outline"
                size="sm"
                disabled={!canWrite || migrating}
                onClick={() => setMigrateOpen(true)}
                title={canWrite ? "" : "Zone de danger — réservée au Premier Admin"}
                className="gap-1.5 border-status-pending/40 text-status-pending hover:bg-status-pending/10"
                data-testid="storage-migrate-btn"
              >
                {migrating
                  ? <Loader2 size={14} className="animate-spin" />
                  : <ArrowRightLeft size={14} />}
                Migrer {data.legacy_pending} fichier{data.legacy_pending > 1 ? "s" : ""} legacy
              </Button>
              <DeskConfirm open={migrateOpen} danger={false} icon={ArrowRightLeft} title="Migrer les fichiers Emergent → Backblaze B2 ?"
                message={`Chaque fichier restant sur l'ancien Object Storage Emergent sera ré-uploadé vers le bucket ${data.bucket}, puis les références en base seront mises à jour. Opération idempotente, relançable sans risque. Fichiers estimés : ${data.legacy_pending}.`}
                confirmLabel="Lancer la migration" onClose={() => setMigrateOpen(false)} onConfirm={() => { setMigrateOpen(false); migrate(); }} testId="storage-migrate-confirm" />
            </>
          )}
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="storage-refresh">
            <RefreshCw size={14} className={"mr-1.5 " + (loading ? "animate-spin" : "")} /> Actualiser
          </Button>
        </div>
      </div>

      {/* --- KPI cards --- */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KpiCard
          label="Volumétrie totale"
          value={fmtBytes(data.total_bytes)}
          hint={`${(gb).toFixed(3)} Go`}
          Icon={HardDrive}
          color="#0078D4"
          testid="kpi-total-bytes"
        />
        <KpiCard
          label="Objets stockés"
          value={data.total_objects?.toLocaleString("fr-FR") || "0"}
          hint="fichiers sur le bucket"
          Icon={FileText}
          color="#8B5CF6"
          testid="kpi-total-objects"
        />
        <KpiCard
          label="Coût mensuel (USD)"
          value={fmtMoney(monthlyUsd, "USD")}
          hint={`≈ ${fmtMoney(monthlyEur, "EUR")}`}
          Icon={DollarSign}
          color="#22C55E"
          testid="kpi-monthly-usd"
        />
        <KpiCard
          label="Projection annuelle"
          value={fmtMoney(monthlyUsd * 12, "USD")}
          hint={`≈ ${fmtMoney(monthlyEur * 12, "EUR")}`}
          Icon={TrendingUp}
          color="#F97316"
          testid="kpi-yearly-usd"
        />
      </div>

      {/* --- Historique mensuel --- */}
      <MonthlyHistory usageSeries={data.history_monthly || []} />

      {/* --- Top sources d'egress (spec 2026-07-23 ext#4) --- */}
      <EgressTopSources />

      {/* --- Top 10 --- */}
      <Top10Files items={data.top_10 || []} />
    </div>
  );
}

/* ---------- KpiCard ---------- */
function KpiCard({ label, value, hint, Icon, color, testid }) {
  return (
    <Card
      className="p-4 bg-card border-vertec-borderDark relative overflow-hidden"
      data-testid={testid}
    >
      <div
        className="absolute top-0 left-0 w-full h-0.5"
        style={{ background: color }}
      />
      <div className="flex items-start justify-between mb-3">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <span
          className="w-8 h-8 rounded-md flex items-center justify-center"
          style={{ background: `${color}22`, color }}
        >
          <Icon size={16} strokeWidth={2.2} />
        </span>
      </div>
      <div className="text-2xl font-bold" style={{ color }}>{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
    </Card>
  );
}

/* ---------- Monthly usageSeries (lightweight bar chart) ---------- */
function MonthlyHistory({ usageSeries }) {
  if (!usageSeries?.length) {
    return (
      <Card className="p-6 bg-card border-vertec-borderDark" data-testid="storage-usageSeries">
        <h3 className="text-base font-semibold mb-2">Évolution mensuelle</h3>
        <p className="text-sm text-muted-foreground">
          Les premières mesures apparaîtront à partir du mois prochain. Un snapshot
          est capturé à chaque consultation de cette page (max 1 par jour).
        </p>
      </Card>
    );
  }
  const maxBytes = Math.max(...usageSeries.map((h) => h.total_bytes || 0), 1);
  return (
    <Card className="p-6 bg-card border-vertec-borderDark" data-testid="storage-usageSeries">
      <h3 className="text-base font-semibold mb-4">Évolution mensuelle — {usageSeries.length} mois</h3>
      <div className="grid grid-flow-col auto-cols-fr gap-2 items-end" style={{ minHeight: 160 }}>
        {usageSeries.map((h) => {
          const pct = ((h.total_bytes || 0) / maxBytes) * 100;
          return (
            <div key={h.month} className="flex flex-col items-center gap-1">
              <div className="text-[10px] font-mono text-muted-foreground">{fmtBytes(h.total_bytes)}</div>
              <div className="w-full flex flex-col-reverse" style={{ height: 120 }}>
                <div
                  className="w-full rounded-t-sm transition-all"
                  style={{ background: "#0078D4", height: `${Math.max(4, pct)}%`, minHeight: 4 }}
                  title={`${h.month} — ${fmtBytes(h.total_bytes)} — ${fmtMoney(h.monthly_usd, "USD")}`}
                />
              </div>
              <div className="text-[10px] text-muted-foreground font-mono">{h.month}</div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/* ---------- Top 10 files ---------- */
function Top10Files({ items }) {
  if (!items?.length) {
    return (
      <Card className="p-6 bg-card border-vertec-borderDark">
        <h3 className="text-base font-semibold mb-2">Top 10 des fichiers les plus lourds</h3>
        <p className="text-sm text-muted-foreground">Aucun fichier stocké pour l&apos;instant.</p>
      </Card>
    );
  }
  return (
    <Card className="p-0 bg-card border-vertec-borderDark overflow-hidden" data-testid="storage-top10">
      <div className="px-6 pt-5 pb-3 border-b border-vertec-borderDark">
        <h3 className="text-base font-semibold">Top 10 des fichiers les plus lourds</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Cliquez sur le lien pour ouvrir la tâche ou le document propriétaire.
        </p>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-vertec-borderDark bg-vertec-borderDark/20">
            <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground w-10">#</th>
            <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground">Fichier / propriétaire</th>
            <th className="text-right px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground">Taille</th>
            <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground">Uploadé le</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, idx) => {
            const owner = it.owner;
            const filename = (it.key || "").split("/").pop();
            let href = null;
            let ownerLabel = null;
            if (owner) {
              if (owner.kind === "task") { href = `/taches/${owner.id}`; ownerLabel = owner.title; }
              else if (owner.kind === "document") { href = `/documents/${owner.id}`; ownerLabel = `${owner.ref || ""} · ${owner.title}`; }
            }
            return (
              <tr key={it.key} className="border-b border-vertec-borderDark/50 hover:bg-vertec-borderDark/10 transition-colors"
                data-testid={`top10-row-${idx}`}>
                <td className="px-4 py-3 text-muted-foreground font-mono">{idx + 1}</td>
                <td className="px-4 py-3">
                  <div className="font-mono text-[12px] text-foreground truncate max-w-[420px]" title={it.key}>{filename}</div>
                  {owner ? (
                    <Link
                      to={href}
                      className="text-[11px] text-vertec-blue hover:underline inline-flex items-center gap-1 mt-0.5"
                      data-testid={`top10-owner-link-${idx}`}
                    >
                      <ExternalLink size={10} />
                      {owner.kind === "task" ? "Tâche" : "Document"} · {ownerLabel}
                    </Link>
                  ) : (
                    <div className="text-[11px] text-status-pending mt-0.5" title="Fichier B2 sans référence en base — orphelin résiduel de migration ou de replace">
                      Orphelin — plus référencé en base
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right font-mono text-[12px]">{fmtBytes(it.size)}</td>
                <td className="px-4 py-3 text-[12px] text-muted-foreground">{fmtDate(it.last_modified)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}
